import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MoveDown } from "lucide-react";
import FloatingIcons from "./FloatingIcons";
import { fetchContent } from "../../lib/content";

const HERO_ILLUSTRATION =
    "https://static.prod-images.emergentagent.com/jobs/67e9be51-e7d1-48d5-867f-e8d5a2d634f6/images/9a241427f8ff50a9f486e559e1924535dea75e267c6ab4ff8ba2fce9e1f2647c.png";

const lineParent = {
    initial: {},
    animate: { transition: { staggerChildren: 0.13, delayChildren: 0.15 } },
};
const lineChild = {
    initial: { y: "110%", opacity: 0 },
    animate: {
        y: "0%",
        opacity: 1,
        transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
    },
};

const CONTENT_KEYS = {
    hero_eyebrow_left: "Commmmuniti / Est. 2019",
    hero_eyebrow_right: "Digital • Performance • Creative",
    hero_line_1: "COMMMMUNITI,",
    hero_line_2: "CONVERSATIONS,",
    hero_line_3: "CONVERSIONS.",
    hero_description:
        "We blend branding, creativity, performance marketing, and web experiences to help brands grow, scale, and stay unforgettable.",
    hero_cta_talk: "LET'S TALK",
    hero_cta_explore: "EXPLORE PROJECTS",
    hero_stats: "120+ brands scaled across 14 industries",
    card_google_title: "Google Ads",
    card_google_value: "+248%",
    card_google_label: "Conversions",
    card_meta_title: "Meta Ads",
    card_meta_value: "+163%",
    card_meta_label: "Qualified Leads",
    card_growth_title: "Growth",
    card_growth_value: "+320%",
    card_growth_label: "YoY Revenue",
    scroll_label: "Scroll",
};

function FloatingCard({ children, className, delay = 0, drift = 12, testid, href }) {
    return (
        <motion.a
            data-testid={testid}
            href={href}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: [0, -drift, 0], scale: 1 }}
            transition={{
                opacity: { duration: 0.8, delay },
                scale: { duration: 0.8, delay },
                y: { duration: 6 + drift / 6, repeat: Infinity, ease: "easeInOut", delay: delay + 0.4 },
            }}
            whileHover={{ scale: 1.04, y: -6 }}
            className={`absolute z-20 select-none rounded-2xl bg-white tile-shadow border border-communiti-line px-4 py-3 ${className}`}
        >
            {children}
        </motion.a>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yIllustration = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const yHeadline = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const opacityHeadline = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

    const [content, setContent] = useState(CONTENT_KEYS);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        Promise.all(
            Object.keys(CONTENT_KEYS).map((key) =>
                fetchContent(key, CONTENT_KEYS[key]).then((value) => ({ key, value }))
            )
        ).then((results) => {
            if (cancelled) return;
            const map = { ...CONTENT_KEYS };
            results.forEach(({ key, value }) => {
                if (value) map[key] = value;
            });
            setContent(map);
            setLoaded(true);
        });
        return () => { cancelled = true; };
    }, []);

    return (
        <section
            id="home"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden bg-white"
        >
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-[0.5]"
                style={{
                    backgroundImage: "radial-gradient(rgba(15,61,122,0.07) 1px, transparent 1px)",
                    backgroundSize: "26px 26px",
                }}
            />
            <FloatingIcons tone="light" />

            <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-10 pt-32 lg:pt-36">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="inline-block h-2 w-2 rounded-full bg-communiti animate-pulse" />
                        <span className="label-eyebrow brand-name">{content.hero_eyebrow_left}</span>
                    </div>
                    <span className="hidden md:block label-eyebrow text-communiti-muted">
                        {content.hero_eyebrow_right}
                    </span>
                </div>
            </div>

            <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-10 pt-10 lg:pt-12 pb-24 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
                <motion.div
                    style={{ y: yHeadline, opacity: opacityHeadline }}
                    className="lg:col-span-6 relative z-10"
                >
                    <motion.h1
                        data-testid="hero-headline"
                        variants={lineParent}
                        initial="initial"
                        animate="animate"
                        className="font-display text-[6.5vw] leading-[1] sm:text-6xl lg:text-[5rem] tracking-[-0.03em] text-communiti-text"
                    >
                        <span className="block">
                                <motion.span variants={lineChild} className="block text-communiti brand-name">
                                    {content.hero_line_1}
                                </motion.span>
                        </span>
                        <span className="block">
                            <motion.span variants={lineChild} className="block">
                                {content.hero_line_2}
                            </motion.span>
                        </span>
                        <span className="block">
                            <motion.span variants={lineChild} className="block">
                                {content.hero_line_3}<span className="text-communiti">.</span>
                            </motion.span>
                        </span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.85, duration: 0.8 }}
                        className="mt-8 max-w-md text-[15px] leading-relaxed text-communiti-muted"
                    >
                        {content.hero_description}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.0, duration: 0.8 }}
                        className="mt-10 flex items-center gap-3 flex-wrap"
                    >
                        <a
                            data-testid="hero-cta-lets-talk"
                            href="#contact"
                            className="group inline-flex items-center gap-2 rounded-full bg-black text-white pl-6 pr-2 py-2.5 text-sm font-medium hover:bg-communiti transition-colors"
                        >
                            {content.hero_cta_talk}
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-black group-hover:rotate-45 transition-transform duration-300">
                                <ArrowUpRight className="h-4 w-4" />
                            </span>
                        </a>
                        <a
                            data-testid="hero-cta-explore"
                            href="#results"
                            className="group inline-flex items-center gap-2 rounded-full border border-communiti-text/15 bg-white pl-6 pr-2 py-2.5 text-sm font-medium hover:border-communiti hover:text-communiti transition-colors"
                        >
                            {content.hero_cta_explore}
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-communiti-surface text-communiti-text group-hover:bg-communiti group-hover:text-white transition-all duration-300">
                                <ArrowUpRight className="h-4 w-4" />
                            </span>
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.3, duration: 0.9 }}
                        className="mt-14 flex items-center gap-6 text-[12px] text-communiti-muted"
                    >
                        <div className="flex -space-x-2">
                            {[1, 2, 3, 4].map((i) => (
                                <span
                                    key={i}
                                    className="h-7 w-7 rounded-full border border-white bg-communiti-surface ring-1 ring-communiti-line"
                                    style={{
                                        backgroundImage: `linear-gradient(135deg, hsl(${i * 80} 30% ${20 + i * 10}%), #0F3D7A)`,
                                    }}
                                />
                            ))}
                        </div>
                        <span>
                            <strong className="text-communiti-text">{content.hero_stats.split(" ")[0]}</strong> {content.hero_stats.split(" ").slice(1).join(" ")}
                        </span>
                    </motion.div>
                </motion.div>

                <motion.div
                    style={{ y: yIllustration }}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-6 relative h-[420px] sm:h-[520px] lg:h-[620px]"
                >
                    <div
                        aria-hidden
                        className="absolute inset-6 rounded-[40%_60%_55%_45%/50%_45%_55%_50%] bg-communiti-surface/80"
                    />
                    <img
                        src={HERO_ILLUSTRATION}
                        alt="Strategists and creators planning a campaign"
                        className="absolute inset-0 h-full w-full object-contain"
                        data-testid="hero-illustration"
                    />

                    <FloatingCard testid="floating-card-google-ads" href="#expertise" className="top-[6%] left-[2%] sm:left-[4%] hidden sm:flex" delay={1.0} drift={10}>
                        <div className="flex items-center gap-3">
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white border border-communiti-line">
                                <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
                                    <path fill="#FBBC05" d="M12.2 4.4 7.1 13.3a3 3 0 1 0 5.2 3l5-8.8a3 3 0 1 0-5.1-3.1Z" />
                                    <circle cx="6.5" cy="17.5" r="3" fill="#34A853" />
                                    <path fill="#4285F4" d="M13.8 4.7 18.9 13.5a3 3 0 1 1-5.2 3l-5-8.7a3 3 0 1 1 5.1-3.1Z" />
                                </svg>
                            </span>
                            <div className="leading-tight">
                                <p className="text-[10.5px] uppercase tracking-[0.18em] text-communiti-muted">{content.card_google_title}</p>
                                <p className="text-lg font-display font-bold text-communiti">{content.card_google_value}</p>
                                <p className="text-[10px] text-communiti-muted">{content.card_google_label}</p>
                            </div>
                        </div>
                    </FloatingCard>

                    <FloatingCard testid="floating-card-meta-ads" href="#expertise" className="top-[14%] right-[2%] sm:right-[4%] hidden sm:flex" delay={1.25} drift={14}>
                        <div className="flex items-center gap-3">
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white border border-communiti-line">
                                <svg viewBox="0 0 36 24" className="h-5 w-7" aria-hidden>
                                    <path fill="#0F3D7A" d="M8.5 4.5C4.9 4.5 2 7.5 2 12.2c0 4.4 2.6 7.3 6 7.3 2.4 0 4.1-1.2 6.8-5l1.6-2.5c.6-1 1.1-1.6 1.7-2.4.5.8 1 1.4 1.7 2.4l1.6 2.5c2.7 3.8 4.4 5 6.8 5 3.4 0 6-2.9 6-7.3 0-4.7-2.9-7.7-6.5-7.7-2.6 0-4.5 1.5-6.6 4.5l-1.4 2.1L19.9 9c-2.1-3-4-4.5-6.6-4.5h-4.8Zm0 3.4h.4c1.5 0 2.9 1.1 4.5 3.4l1.4 2-1.6 2.4c-1.9 2.7-3.1 3.6-4.5 3.6-1.6 0-3-1.4-3-3.9 0-3.4 1.5-5.5 2.8-7.5Zm19 0c1.3 2 2.8 4.1 2.8 7.5 0 2.5-1.4 3.9-3 3.9-1.4 0-2.6-.9-4.5-3.6L21.2 13.3l1.4-2c1.6-2.3 3-3.4 4.5-3.4h.4Z" />
                                </svg>
                            </span>
                            <div className="leading-tight">
                                <p className="text-[10.5px] uppercase tracking-[0.18em] text-communiti-muted">{content.card_meta_title}</p>
                                <p className="text-lg font-display font-bold text-communiti">{content.card_meta_value}</p>
                                <p className="text-[10px] text-communiti-muted">{content.card_meta_label}</p>
                            </div>
                        </div>
                    </FloatingCard>

                    <FloatingCard testid="floating-card-growth" href="#results" className="bottom-[6%] right-[4%] sm:right-[8%] hidden sm:flex" delay={1.5} drift={12}>
                        <div className="flex items-center gap-3">
                            <svg viewBox="0 0 60 32" className="h-7 w-16">
                                <polyline fill="none" stroke="#0F3D7A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points="2,28 12,22 22,24 32,14 42,17 58,4" />
                                <circle cx="58" cy="4" r="3" fill="#0F3D7A" />
                            </svg>
                            <div className="leading-tight">
                                <p className="text-[10.5px] uppercase tracking-[0.18em] text-communiti-muted">{content.card_growth_title}</p>
                                <p className="text-lg font-display font-bold text-communiti">{content.card_growth_value}</p>
                                <p className="text-[10px] text-communiti-muted">{content.card_growth_label}</p>
                            </div>
                        </div>
                    </FloatingCard>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-communiti-muted"
            >
                <span className="text-[10px] uppercase tracking-[0.3em]">{content.scroll_label}</span>
                <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
                    <MoveDown className="h-4 w-4" />
                </motion.span>
            </motion.div>
        </section>
    );
}
